import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  CloudOff,
  ExternalLink,
  FolderSync,
  HardDrive,
  Loader2,
  Pencil,
  Unplug,
  Users,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type { SharedDriveItem } from '@/services/api/googleDriveService';
import {
  useDisconnectGoogleDriveMutation,
  useGetGoogleDriveStatusQuery,
  useListGoogleDriveSharedDrivesQuery,
  useUpdateGoogleDriveRootFolderMutation,
  useUpdateGoogleDriveSharedDriveMutation,
} from '@/services/api/googleDriveService';

const BACKEND_HOST =
  import.meta.env.VITE_APP_DJANGO_BACKEND_HOST || 'http://localhost:8000';

interface GoogleDriveIntegrationProps {
  firmId: string;
}

const GoogleDriveIntegrationSkeleton: React.FC = () => (
  <div className="border rounded-lg bg-white overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <Skeleton className="h-6 w-24 rounded-full" />
    </div>
  </div>
);

const GoogleDriveLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 87.3 78"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L27.5 51H0c0 1.55.4 3.1 1.2 4.5z"
      fill="#0066da"
    />
    <path
      d="M43.65 25L29.9 1.2C28.55 2 27.4 3.1 26.6 4.5L1.2 46.5C.4 47.9 0 49.45 0 51h27.5z"
      fill="#00ac47"
    />
    <path
      d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H60l5.85 10.5z"
      fill="#ea4335"
    />
    <path
      d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z"
      fill="#00832d"
    />
    <path
      d="M60 51H27.5L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50.5c1.6 0 3.15-.4 4.5-1.2z"
      fill="#2684fc"
    />
    <path
      d="M73.4 25.5L60.7 4.5c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25 60 51h27.45c0-1.55-.4-3.1-1.2-4.5z"
      fill="#ffba00"
    />
  </svg>
);

export const GoogleDriveIntegration: React.FC<GoogleDriveIntegrationProps> = ({
  firmId,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
  const [isEditingRootFolder, setIsEditingRootFolder] = useState(false);
  const [editRootFolderId, setEditRootFolderId] = useState('');
  const [isSharedDriveDialogOpen, setIsSharedDriveDialogOpen] = useState(false);

  const {
    data: status,
    isLoading,
    refetch,
  } = useGetGoogleDriveStatusQuery(
    { firmId },
    { refetchOnMountOrArgChange: true },
  );

  const {
    data: sharedDrivesData,
    isLoading: isLoadingSharedDrives,
    isError: isSharedDrivesError,
  } = useListGoogleDriveSharedDrivesQuery(
    { firmId },
    { skip: !status?.connected || !isSharedDriveDialogOpen },
  );

  const [disconnectGoogleDrive, { isLoading: isDisconnecting }] =
    useDisconnectGoogleDriveMutation();

  const [updateRootFolder, { isLoading: isUpdatingRootFolder }] =
    useUpdateGoogleDriveRootFolderMutation();

  const [updateSharedDrive, { isLoading: isUpdatingSharedDrive }] =
    useUpdateGoogleDriveSharedDriveMutation();

  // Show success toast when redirected back from OAuth
  useEffect(() => {
    if (searchParams.get('google_drive') === 'connected') {
      toast.success('Google Drive connected successfully');
      searchParams.delete('google_drive');
      setSearchParams(searchParams, { replace: true });
      refetch();
    }
  }, [searchParams, setSearchParams, refetch]);

  const handleConnect = () => {
    window.location.href = `${BACKEND_HOST}/api/integrations/google-drive/connect?firm_id=${firmId}`;
  };

  const handleSaveRootFolder = async () => {
    try {
      await updateRootFolder({
        firmId,
        rootFolderId: editRootFolderId.trim(),
      }).unwrap();
      toast.success('Root folder updated');
      setIsEditingRootFolder(false);
    } catch {
      toast.error('Failed to update root folder');
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectGoogleDrive({ firmId }).unwrap();
      toast.success('Google Drive disconnected');
      setIsDisconnectDialogOpen(false);
    } catch {
      toast.error('Failed to disconnect Google Drive');
    }
  };

  const handleSelectSharedDrive = async (drive: SharedDriveItem | null) => {
    try {
      await updateSharedDrive({
        firmId,
        sharedDriveId: drive?.id ?? '',
        sharedDriveName: drive?.name ?? '',
      }).unwrap();
      toast.success(drive ? `Switched to "${drive.name}"` : 'Reverted to My Drive');
      setIsSharedDriveDialogOpen(false);
    } catch {
      toast.error('Failed to update Shared Drive');
    }
  };

  if (isLoading) {
    return <GoogleDriveIntegrationSkeleton />;
  }

  const isConnected = status?.connected;

  return (
    <>
      <div className="border rounded-lg bg-white overflow-hidden">
        {/* Card header: logo, name, description, status */}
        <div className="flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-50 border border-gray-100">
              <GoogleDriveLogo className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900">
                  Google Drive
                </h3>
                <span className="text-xs text-gray-500">
                  Cloud document storage
                </span>
              </div>
              {isConnected ? (
                <p className="text-xs text-gray-500 mt-0.5">
                  {status.display_name || 'Google Account'}
                  {status.email ? ` \u00B7 ${status.email}` : ''}
                </p>
              ) : (
                <p className="text-xs text-gray-500 mt-0.5">
                  Sync documents between Finch and Google Drive
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isConnected ? (
              <>
                <Badge
                  variant="default"
                  className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border border-emerald-200 font-medium"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Connected
                </Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsDisconnectDialogOpen(true)}
                  className="text-gray-500 hover:text-red-600 h-8 px-2"
                >
                  <Unplug className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <Button onClick={handleConnect} size="sm" className="h-8">
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Connect
              </Button>
            )}
          </div>
        </div>

        {/* Connected: sync status + configuration */}
        {isConnected && (
          <div className="border-t bg-blue-50/50 px-5 py-3 space-y-2.5">
            {/* Sync active banner */}
            <div className="flex items-center gap-2 text-xs text-blue-700">
              <FolderSync className="h-3.5 w-3.5 shrink-0 text-blue-600" />
              <span className="font-medium">Document sync is active.</span>
              <span className="text-blue-600">
                Configure push rules in the Document Sync tab.
              </span>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-y-2">
              {/* Shared Drive selector */}
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500">Drive scope:</span>
                <button
                  type="button"
                  onClick={() => setIsSharedDriveDialogOpen(true)}
                  className="flex items-center gap-1 text-gray-700 hover:text-gray-900"
                >
                  {status.shared_drive_id ? (
                    <>
                      <Users className="h-3 w-3 text-blue-600 shrink-0" />
                      <span className="max-w-[180px] truncate font-medium">
                        {status.shared_drive_name || status.shared_drive_id}
                      </span>
                    </>
                  ) : (
                    <>
                      <HardDrive className="h-3 w-3 text-gray-500 shrink-0" />
                      <span className="text-gray-500 italic">My Drive</span>
                    </>
                  )}
                  <ChevronDown className="h-2.5 w-2.5 text-gray-400 shrink-0" />
                </button>
              </div>

              {/* Root folder ID */}
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-gray-500">Root folder:</span>
                {isEditingRootFolder ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={editRootFolderId}
                      onChange={(e) => setEditRootFolderId(e.target.value)}
                      placeholder="Folder ID or leave empty for root"
                      className="h-6 text-xs font-mono w-52 px-1.5"
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0"
                      onClick={handleSaveRootFolder}
                      disabled={isUpdatingRootFolder}
                    >
                      {isUpdatingRootFolder ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Check className="h-3 w-3 text-green-600" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-1 text-xs text-gray-500"
                      onClick={() => setIsEditingRootFolder(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setEditRootFolderId(status.root_folder_id || '');
                      setIsEditingRootFolder(true);
                    }}
                    className="flex items-center gap-1 text-gray-700 hover:text-gray-900 font-mono"
                  >
                    {status.root_folder_id ? (
                      <span className="max-w-[160px] truncate">
                        {status.root_folder_id}
                      </span>
                    ) : (
                      <span className="text-gray-500 italic">
                        {status.shared_drive_id ? 'Drive root' : 'My Drive'}
                      </span>
                    )}
                    <Pencil className="h-2.5 w-2.5 text-gray-500 shrink-0" />
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Disconnected: empty state */}
        {!isConnected && (
          <div className="border-t bg-gray-50/50 px-5 py-3">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <CloudOff className="h-3.5 w-3.5 shrink-0" />
              <span>
                No account connected. Click Connect to authorize via Google
                OAuth. Finch will sync documents from the selected Drive folder.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Shared Drive picker dialog */}
      <Dialog
        open={isSharedDriveDialogOpen}
        onOpenChange={setIsSharedDriveDialogOpen}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Drive Scope</DialogTitle>
            <DialogDescription>
              Choose a Shared Drive to scope document sync, or use My Drive
              (the authenticated user&apos;s personal drive).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1 max-h-72 overflow-y-auto">
            {/* My Drive option */}
            <button
              type="button"
              onClick={() => handleSelectSharedDrive(null)}
              disabled={isUpdatingSharedDrive}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-left transition-colors ${
                !status?.shared_drive_id
                  ? 'bg-blue-50 text-blue-800 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <HardDrive className="h-4 w-4 shrink-0 text-gray-500" />
              <div>
                <div className="font-medium">My Drive</div>
                <div className="text-xs text-gray-500">
                  Personal drive of the connected Google account
                </div>
              </div>
              {!status?.shared_drive_id && (
                <Check className="h-4 w-4 ml-auto text-blue-600 shrink-0" />
              )}
            </button>

            {/* Loading state */}
            {isLoadingSharedDrives && (
              <div className="flex items-center justify-center py-6 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Loading Shared Drives…
              </div>
            )}

            {/* Error state */}
            {!isLoadingSharedDrives && isSharedDrivesError && (
              <div className="px-3 py-6 text-center text-sm text-red-500">
                Failed to load Shared Drives. Please try again.
              </div>
            )}

            {/* Shared Drive options */}
            {!isLoadingSharedDrives &&
              sharedDrivesData?.drives?.map((drive) => (
                <button
                  key={drive.id}
                  type="button"
                  onClick={() => handleSelectSharedDrive(drive)}
                  disabled={isUpdatingSharedDrive}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-left transition-colors ${
                    status?.shared_drive_id === drive.id
                      ? 'bg-blue-50 text-blue-800 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Users className="h-4 w-4 shrink-0 text-blue-500" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{drive.name}</div>
                    <div className="text-xs text-gray-500 font-mono truncate">
                      {drive.id}
                    </div>
                  </div>
                  {status?.shared_drive_id === drive.id && (
                    <Check className="h-4 w-4 ml-auto text-blue-600 shrink-0" />
                  )}
                </button>
              ))}

            {/* Empty state: no shared drives */}
            {!isLoadingSharedDrives &&
              !isSharedDrivesError &&
              sharedDrivesData?.drives?.length === 0 && (
                <div className="px-3 py-6 text-center text-sm text-gray-500">
                  No Shared Drives found for this account.
                </div>
              )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsSharedDriveDialogOpen(false)}
              disabled={isUpdatingSharedDrive}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disconnect confirmation dialog */}
      <Dialog
        open={isDisconnectDialogOpen}
        onOpenChange={setIsDisconnectDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Google Drive</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect this Google Drive account?
              Document sync will stop, but existing synced documents will remain
              in both Finch and Google Drive.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertCircle className="h-5 w-5 mt-0.5 shrink-0 text-amber-600" />
            <p>
              Any in-progress sync operations will be cancelled. You can
              reconnect at any time.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDisconnectDialogOpen(false)}
              disabled={isDisconnecting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisconnect}
              disabled={isDisconnecting}
            >
              {isDisconnecting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disconnecting...
                </>
              ) : (
                'Disconnect'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
